import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Autocomplete,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import { RTL } from "../../cache/CacheRtl";
import { getCompanies } from "../../services/system/CompanyService";
import { getTrainers } from "../../services/TrainerService";
import {
    saveScheduledNotification,
    updateScheduledNotification,
    AUDIENCE_TYPES,
    NOTIFICATION_SCOPES,
    DAYS_OF_WEEK
} from "../../services/ScheduledNotificationService";
import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";
import UserMultiSelectDialog from "../shared/UserMultiSelectDialog";
import CompanyMultiSelectDialog from "../shared/CompanyMultiSelectDialog";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';

/**
 * ScheduledNotificationDialog
 *
 * Permissions:
 * - Super Admin: Can create GLOBAL, MULTI_COMPANY, SINGLE_COMPANY, TRAINER notifications
 * - Company Admin: Can create SINGLE_COMPANY (their company) or TRAINER notifications
 * - Trainer: Can only create TRAINER notifications for their own trainees
 */
const ScheduledNotificationDialog = ({ isOpen, close, setReload, editNotification = null }) => {
    const { isSuperAdmin, isCompanyAdmin, isTrainer } = useAuth();
    const { company } = useCompany();

    const [formData, setFormData] = useState({
        title: "",
        body: "",
        scope: NOTIFICATION_SCOPES.SINGLE_COMPANY,
        audienceType: AUDIENCE_TYPES.ALL_USERS,
        scheduleDays: [],
        scheduleTime: "09:00",
        companyId: "",
        trainerId: ""
    });

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [trainers, setTrainers] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [userSelectOpen, setUserSelectOpen] = useState(false);
    const [companySelectOpen, setCompanySelectOpen] = useState(false);

    const isEdit = editNotification !== null;

    // Get available scopes based on user role
    const getAvailableScopes = () => {
        if (isSuperAdmin()) {
            return [
                { value: NOTIFICATION_SCOPES.GLOBAL, label: 'כל החברות במערכת' },
                { value: NOTIFICATION_SCOPES.MULTI_COMPANY, label: 'חברות נבחרות' },
                { value: NOTIFICATION_SCOPES.SINGLE_COMPANY, label: 'חברה בודדת' },
                { value: NOTIFICATION_SCOPES.TRAINER, label: 'מתאמני מאמן ספציפי' }
            ];
        }
        if (isCompanyAdmin()) {
            return [
                { value: NOTIFICATION_SCOPES.SINGLE_COMPANY, label: 'כל החברה' },
                { value: NOTIFICATION_SCOPES.TRAINER, label: 'מתאמני מאמן ספציפי' }
            ];
        }
        // Trainer only
        return [
            { value: NOTIFICATION_SCOPES.TRAINER, label: 'המתאמנים שלי' }
        ];
    };

    // Get default scope based on role
    const getDefaultScope = () => {
        if (isTrainer() && !isCompanyAdmin()) return NOTIFICATION_SCOPES.TRAINER;
        return NOTIFICATION_SCOPES.SINGLE_COMPANY;
    };

    useEffect(() => {
        if (isOpen) {
            if (isSuperAdmin()) {
                loadCompanies();
            }
            if (isEdit) {
                populateFormForEdit();
            } else {
                resetForm();
            }
        }
    }, [isOpen, editNotification]);

    // Load trainers when company changes
    useEffect(() => {
        const companyId = isSuperAdmin() ? formData.companyId : company?.id;
        if (isOpen && companyId) {
            loadTrainers(companyId);
        }
    }, [isOpen, formData.companyId, company?.id]);

    const loadCompanies = async () => {
        try {
            const response = await getCompanies();
            setCompanies(response.data || []);
        } catch (error) {
            console.error("Error loading companies:", error);
        }
    };

    const loadTrainers = async (companyId) => {
        try {
            const response = await getTrainers(companyId);
            setTrainers(response.data || []);
        } catch (error) {
            console.error("Error loading trainers:", error);
        }
    };

    const populateFormForEdit = () => {
        const scope = editNotification.scope || getDefaultScope();
        setFormData({
            title: editNotification.title || "",
            body: editNotification.body || "",
            scope: scope,
            audienceType: editNotification.audienceType || AUDIENCE_TYPES.ALL_USERS,
            scheduleDays: editNotification.scheduleDays || [],
            scheduleTime: editNotification.scheduleTime || "09:00",
            companyId: editNotification.companyId || "",
            trainerId: editNotification.trainerId || ""
        });

        // Set selected users
        if (editNotification.targetUsers?.length > 0) {
            setSelectedUsers(editNotification.targetUsers);
        }

        // Set selected companies for MULTI_COMPANY scope
        if (editNotification.targetCompanies?.length > 0) {
            setSelectedCompanies(editNotification.targetCompanies);
        }
    };

    // Update selected company/trainer after companies/trainers are loaded
    useEffect(() => {
        if (isEdit && companies.length > 0 && formData.companyId) {
            const comp = companies.find(c => c.id === formData.companyId);
            setSelectedCompany(comp || null);
        }
    }, [companies, formData.companyId, isEdit]);

    useEffect(() => {
        if (isEdit && trainers.length > 0 && formData.trainerId) {
            const trainer = trainers.find(t => t.id === formData.trainerId);
            setSelectedTrainer(trainer || null);
        }
    }, [trainers, formData.trainerId, isEdit]);

    const resetForm = () => {
        const defaultScope = getDefaultScope();
        setFormData({
            title: "",
            body: "",
            scope: defaultScope,
            audienceType: AUDIENCE_TYPES.ALL_USERS,
            scheduleDays: [],
            scheduleTime: "09:00",
            companyId: "",
            // Empty for everyone: a plain trainer never picks a trainer, and their own is resolved
            // server-side from the principal (their user id is the wrong id space to put here).
            trainerId: ""
        });
        setSelectedUsers([]);
        setSelectedCompanies([]);
        setSelectedCompany(null);
        setSelectedTrainer(null);
        setErrors({});
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = "יש להזין כותרת";
        } else if (formData.title.length > 100) {
            newErrors.title = "הכותרת יכולה להכיל עד 100 תווים";
        }

        if (!formData.body.trim()) {
            newErrors.body = "יש להזין תוכן ההודעה";
        } else if (formData.body.length > 500) {
            newErrors.body = "תוכן ההודעה יכול להכיל עד 500 תווים";
        }

        if (formData.scheduleDays.length === 0) {
            newErrors.scheduleDays = "יש לבחור לפחות יום אחד";
        }

        if (!formData.scheduleTime) {
            newErrors.scheduleTime = "יש לבחור שעה";
        }

        // Scope-specific validation
        switch (formData.scope) {
            case NOTIFICATION_SCOPES.MULTI_COMPANY:
                if (selectedCompanies.length === 0) {
                    newErrors.targetCompanies = "יש לבחור לפחות חברה אחת";
                }
                break;
            case NOTIFICATION_SCOPES.SINGLE_COMPANY:
                if (isSuperAdmin() && !formData.companyId) {
                    newErrors.companyId = "יש לבחור חברה";
                }
                break;
            case NOTIFICATION_SCOPES.TRAINER:
                if (isSuperAdmin() && !formData.companyId) {
                    newErrors.companyId = "יש לבחור חברה";
                }
                if ((isSuperAdmin() || isCompanyAdmin()) && !formData.trainerId) {
                    newErrors.trainerId = "יש לבחור מאמן";
                }
                break;
        }

        // Audience type validation
        if (formData.audienceType === AUDIENCE_TYPES.SPECIFIC_USERS && selectedUsers.length === 0) {
            newErrors.targetUsers = "יש לבחור לפחות משתמש אחד";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const submitData = {
                title: formData.title.trim(),
                body: formData.body.trim(),
                scope: formData.scope,
                audienceType: formData.audienceType,
                scheduleDays: formData.scheduleDays,
                scheduleTime: formData.scheduleTime,
                isActive: true
            };

            // Handle scope-specific data
            switch (formData.scope) {
                case NOTIFICATION_SCOPES.GLOBAL:
                    // No additional fields needed - system-wide
                    break;

                case NOTIFICATION_SCOPES.MULTI_COMPANY:
                    submitData.targetCompanyIds = selectedCompanies.map(c => c.id);
                    break;

                case NOTIFICATION_SCOPES.SINGLE_COMPANY:
                    submitData.companyId = isSuperAdmin() ? formData.companyId : company?.id;
                    break;

                case NOTIFICATION_SCOPES.TRAINER:
                    submitData.companyId = isSuperAdmin() ? formData.companyId : company?.id;
                    // trainerId is a TRAINER id (what the dropdown holds and what the server reads back),
                    // never a user id. A plain trainer creating their own has no way to know it, so they
                    // send nothing and the server resolves the trainer from the logged-in principal.
                    submitData.trainerId = (isTrainer() && !isCompanyAdmin()) ? null : formData.trainerId;
                    break;
            }

            // Add target users if specific users selected
            if (formData.audienceType === AUDIENCE_TYPES.SPECIFIC_USERS) {
                submitData.targetUserIds = selectedUsers.map(u => u.id);
            }

            if (isEdit) {
                await updateScheduledNotification(editNotification.id, submitData);
            } else {
                await saveScheduledNotification(submitData);
            }

            setReload();
            close();
            resetForm();
        } catch (error) {
            console.error("Error saving notification:", error);
            const errorMessage = error.response?.data?.message || "שגיאה בשמירת ההתראה";
            setErrors({ submit: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        close();
        resetForm();
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    const handleScopeChange = (newScope) => {
        handleInputChange('scope', newScope);
        // Reset dependent fields
        setSelectedUsers([]);
        setSelectedCompanies([]);
        handleInputChange('audienceType', AUDIENCE_TYPES.ALL_USERS);

        if (newScope !== NOTIFICATION_SCOPES.TRAINER) {
            handleInputChange('trainerId', '');
            setSelectedTrainer(null);
        }
        if (newScope === NOTIFICATION_SCOPES.GLOBAL || newScope === NOTIFICATION_SCOPES.MULTI_COMPANY) {
            handleInputChange('companyId', '');
            setSelectedCompany(null);
        }
    };

    const handleDaysChange = (event, newDays) => {
        handleInputChange('scheduleDays', newDays || []);
    };

    const handleUserSelectConfirm = (users) => {
        setSelectedUsers(users);
        setUserSelectOpen(false);
        if (errors.targetUsers) {
            setErrors(prev => ({ ...prev, targetUsers: "" }));
        }
    };

    const handleCompanySelectConfirm = (companies) => {
        setSelectedCompanies(companies);
        setCompanySelectOpen(false);
        if (errors.targetCompanies) {
            setErrors(prev => ({ ...prev, targetCompanies: "" }));
        }
    };

    // Get company for UserMultiSelectDialog
    const getUserSelectCompany = () => {
        if (formData.scope === NOTIFICATION_SCOPES.GLOBAL) return null;
        if (formData.scope === NOTIFICATION_SCOPES.MULTI_COMPANY) return null;
        if (isSuperAdmin()) return selectedCompany;
        return company;
    };

    // Get trainer ID for filtering in UserMultiSelectDialog. Same id space as everywhere else here
    // (a TRAINER id) — for a plain trainer we send none, since the trainee search is already forced
    // to their own trainees server-side.
    const getUserSelectTrainerId = () => {
        if (formData.scope === NOTIFICATION_SCOPES.TRAINER) {
            if (isTrainer() && !isCompanyAdmin()) return null;
            return formData.trainerId || null;
        }
        return null;
    };

    // Check if user selection is possible
    // GLOBAL scope does not allow specific user selection - too broad
    const canSelectUsers = () => {
        switch (formData.scope) {
            case NOTIFICATION_SCOPES.GLOBAL:
                return false; // Global scope = all users, no specific selection allowed
            case NOTIFICATION_SCOPES.MULTI_COMPANY:
                return selectedCompanies.length > 0;
            case NOTIFICATION_SCOPES.SINGLE_COMPANY:
                return isSuperAdmin() ? !!formData.companyId : true;
            case NOTIFICATION_SCOPES.TRAINER:
                if (isTrainer() && !isCompanyAdmin()) return true;
                return !!formData.trainerId;
            default:
                return false;
        }
    };

    // Get audience "all users" label based on scope
    const getAudienceAllLabel = () => {
        switch (formData.scope) {
            case NOTIFICATION_SCOPES.GLOBAL:
                return "כל המשתמשים בכל החברות";
            case NOTIFICATION_SCOPES.MULTI_COMPANY:
                return "כל המשתמשים בחברות הנבחרות";
            case NOTIFICATION_SCOPES.SINGLE_COMPANY:
                return "כל המשתמשים בחברה";
            case NOTIFICATION_SCOPES.TRAINER:
                return (isTrainer() && !isCompanyAdmin()) ? "כל המתאמנים שלי" : "כל המתאמנים של המאמן";
            default:
                return "כל המשתמשים";
        }
    };

    // Show scope selection only if user has multiple options and not editing
    const showScopeSelection = !isEdit && getAvailableScopes().length > 1;

    return (
        <>
            <UserMultiSelectDialog
                isOpen={userSelectOpen}
                onClose={() => setUserSelectOpen(false)}
                onConfirm={handleUserSelectConfirm}
                title="בחירת מתאמנים להתראה"
                confirmButtonText="אישור בחירה"
                excludeUserIds={[]}
                companyOverride={getUserSelectCompany()}
                trainerIdOverride={getUserSelectTrainerId()}
            />

            <CompanyMultiSelectDialog
                isOpen={companySelectOpen}
                onClose={() => setCompanySelectOpen(false)}
                onConfirm={handleCompanySelectConfirm}
                title="בחירת חברות להתראה"
                confirmButtonText="אישור בחירה"
                showAllCompaniesOption={false}
                initialSelectedCompanies={selectedCompanies}
            />

            <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth dir="rtl">
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        {isEdit ? "עריכת התראה מתוזמנת" : "הוספת התראה מתוזמנת חדשה"}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>

                        {/* Scope Selection */}
                        {showScopeSelection && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                    היקף ההתראה *
                                </Typography>
                                <FormControl component="fieldset">
                                    <RadioGroup
                                        value={formData.scope}
                                        onChange={(e) => handleScopeChange(e.target.value)}
                                    >
                                        {getAvailableScopes().map((scope) => (
                                            <FormControlLabel
                                                key={scope.value}
                                                value={scope.value}
                                                control={<Radio sx={{ '&.Mui-checked': { color: '#57837E' } }} />}
                                                label={scope.label}
                                                disabled={loading}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        )}

                        {/* Multi-Company Selection (MULTI_COMPANY scope) */}
                        {formData.scope === NOTIFICATION_SCOPES.MULTI_COMPANY && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: errors.targetCompanies ? 'error.main' : 'text.secondary' }}>
                                        חברות נבחרות *
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setCompanySelectOpen(true)}
                                        disabled={loading}
                                        startIcon={<BusinessIcon />}
                                        sx={{ borderColor: '#57837E', color: '#57837E', '&:hover': { borderColor: '#4a726d', backgroundColor: 'rgba(87, 131, 126, 0.04)' } }}
                                    >
                                        בחר חברות
                                    </Button>
                                </Box>

                                {selectedCompanies.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, backgroundColor: '#EFF6FF', borderRadius: '8px' }}>
                                        {selectedCompanies.map(comp => (
                                            <RTL key={comp.id}>
                                                <Chip
                                                    label={comp.name}
                                                    onDelete={() => setSelectedCompanies(prev => prev.filter(c => c.id !== comp.id))}
                                                    deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                                                    icon={<BusinessIcon sx={{ fontSize: '14px !important' }} />}
                                                    size="small"
                                                    sx={{ backgroundColor: '#DBEAFE', color: '#1E40AF', fontSize: '12px', '& .MuiChip-deleteIcon': { color: '#1E40AF' } }}
                                                />
                                            </RTL>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box sx={{ p: 3, backgroundColor: '#F8FAFC', borderRadius: '8px', textAlign: 'center', border: errors.targetCompanies ? '1px solid #EF4444' : '1px dashed #E2E8F0' }}>
                                        <Typography variant="body2" color="text.secondary">לא נבחרו חברות</Typography>
                                    </Box>
                                )}
                                {errors.targetCompanies && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.targetCompanies}</Typography>
                                )}
                            </Box>
                        )}

                        {/* Company Selection (SINGLE_COMPANY or TRAINER scope - Super Admin only) */}
                        {(formData.scope === NOTIFICATION_SCOPES.SINGLE_COMPANY || formData.scope === NOTIFICATION_SCOPES.TRAINER) && isSuperAdmin() && (
                            <RTL>
                                <Autocomplete
                                    value={selectedCompany}
                                    onChange={(event, newValue) => {
                                        setSelectedCompany(newValue);
                                        handleInputChange('companyId', newValue?.id || '');
                                        setSelectedTrainer(null);
                                        handleInputChange('trainerId', '');
                                        setSelectedUsers([]);
                                    }}
                                    options={companies}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField {...params} label="חברה *" error={!!errors.companyId} helperText={errors.companyId} fullWidth />
                                    )}
                                    disabled={loading}
                                />
                            </RTL>
                        )}

                        {/* Trainer Selection (TRAINER scope - Super Admin and Company Admin) */}
                        {formData.scope === NOTIFICATION_SCOPES.TRAINER && (isSuperAdmin() || isCompanyAdmin()) && (
                            <RTL>
                                <Autocomplete
                                    value={selectedTrainer}
                                    onChange={(event, newValue) => {
                                        setSelectedTrainer(newValue);
                                        handleInputChange('trainerId', newValue?.id || '');
                                        setSelectedUsers([]);
                                    }}
                                    options={trainers}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField {...params} label="מאמן *" error={!!errors.trainerId} helperText={errors.trainerId} fullWidth />
                                    )}
                                    disabled={loading || (isSuperAdmin() && !formData.companyId)}
                                />
                            </RTL>
                        )}

                        {/* Title */}
                        <RTL>
                            <TextField
                                label="כותרת ההתראה *"
                                value={formData.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                error={!!errors.title}
                                helperText={errors.title || `${formData.title.length}/100`}
                                fullWidth
                                disabled={loading}
                                inputProps={{ maxLength: 100 }}
                            />
                        </RTL>

                        {/* Body */}
                        <RTL>
                            <TextField
                                label="תוכן ההודעה *"
                                value={formData.body}
                                onChange={(e) => handleInputChange('body', e.target.value)}
                                error={!!errors.body}
                                helperText={errors.body || `${formData.body.length}/500`}
                                fullWidth
                                multiline
                                rows={3}
                                disabled={loading}
                                inputProps={{ maxLength: 500 }}
                            />
                        </RTL>

                        {/* Schedule Days */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: errors.scheduleDays ? 'error.main' : 'text.secondary' }}>
                                ימים לשליחה *
                            </Typography>
                            <ToggleButtonGroup
                                value={formData.scheduleDays}
                                onChange={handleDaysChange}
                                aria-label="schedule days"
                                sx={{
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '8px !important',
                                        px: 2,
                                        py: 1,
                                        '&.Mui-selected': { backgroundColor: '#57837E', color: 'white', '&:hover': { backgroundColor: '#4a726d' } }
                                    }
                                }}
                            >
                                {DAYS_OF_WEEK.map((day) => (
                                    <ToggleButton key={day.value} value={day.value} disabled={loading}>
                                        {day.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            {errors.scheduleDays && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.scheduleDays}</Typography>
                            )}
                        </Box>

                        {/* Schedule Time */}
                        <RTL>
                            <TextField
                                label="שעת שליחה *"
                                type="time"
                                value={formData.scheduleTime}
                                onChange={(e) => handleInputChange('scheduleTime', e.target.value)}
                                error={!!errors.scheduleTime}
                                helperText={errors.scheduleTime}
                                fullWidth
                                disabled={loading}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiOutlinedInput-input': { textAlign: 'end' } }}
                            />
                        </RTL>

                        {/* Audience Type */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                קהל יעד
                            </Typography>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    value={formData.audienceType}
                                    onChange={(e) => handleInputChange('audienceType', e.target.value)}
                                >
                                    <FormControlLabel
                                        value={AUDIENCE_TYPES.ALL_USERS}
                                        control={<Radio sx={{ '&.Mui-checked': { color: '#57837E' } }} />}
                                        label={getAudienceAllLabel()}
                                        disabled={loading}
                                    />
                                    <FormControlLabel
                                        value={AUDIENCE_TYPES.SPECIFIC_USERS}
                                        control={<Radio sx={{ '&.Mui-checked': { color: '#57837E' } }} />}
                                        label="משתמשים ספציפיים"
                                        disabled={loading || !canSelectUsers()}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        {/* Selected Users Section */}
                        {formData.audienceType === AUDIENCE_TYPES.SPECIFIC_USERS && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: errors.targetUsers ? 'error.main' : 'text.secondary' }}>
                                        משתמשים נבחרים
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setUserSelectOpen(true)}
                                        disabled={loading || !canSelectUsers()}
                                        sx={{ borderColor: '#57837E', color: '#57837E', '&:hover': { borderColor: '#4a726d', backgroundColor: 'rgba(87, 131, 126, 0.04)' } }}
                                    >
                                        בחר מתאמנים
                                    </Button>
                                </Box>

                                {selectedUsers.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, backgroundColor: '#F0FDF4', borderRadius: '8px' }}>
                                        {selectedUsers.map(user => (
                                            <RTL key={user.id}>
                                                <Chip
                                                    label={user.name}
                                                    onDelete={() => setSelectedUsers(prev => prev.filter(u => u.id !== user.id))}
                                                    deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                                                    icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                                    size="small"
                                                    sx={{ backgroundColor: '#DCFCE7', color: '#166534', fontSize: '12px', '& .MuiChip-deleteIcon': { color: '#166534', '&:hover': { color: '#14532D' } } }}
                                                />
                                            </RTL>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box sx={{ p: 3, backgroundColor: '#F8FAFC', borderRadius: '8px', textAlign: 'center', border: errors.targetUsers ? '1px solid #EF4444' : '1px dashed #E2E8F0' }}>
                                        <Typography variant="body2" color="text.secondary">לא נבחרו מתאמנים</Typography>
                                    </Box>
                                )}
                                {errors.targetUsers && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.targetUsers}</Typography>
                                )}
                            </Box>
                        )}

                        {/* Submit Error */}
                        {errors.submit && (
                            <Typography color="error" variant="body2">{errors.submit}</Typography>
                        )}

                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleClose} disabled={loading} variant="outlined">
                        ביטול
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading}
                        variant="contained"
                        sx={{ minWidth: 120, backgroundColor: '#57837E', '&:hover': { backgroundColor: '#4a726d' } }}
                    >
                        {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (isEdit ? "עדכן" : "צור התראה")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ScheduledNotificationDialog;
